import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { LuMapPin, LuX, LuKeySquare, LuCalendar, LuLink2, LuUndo2, LuTags, LuHistory } from 'react-icons/lu'
import useMergedData from '../hooks/useMergedData'
import useComputeNodes from '../hooks/useComputeNodes'
import ComputeBadge from './ComputeBadge'
import { NodeVersionBanner } from './NodeVersionMark'
import { useOrgRole } from '../hooks/useOrgRole'
import { NODE_FORBIDDEN, nodeRemoveRefusal, toolRefusalMessage } from '../utilities/routerWrites'
import useToken from '../models/useToken'
import useNode, { isNodeOffline } from '../models/useNode'
import RenameTitle from './RenameTitle'
import useChannel from '../models/useChannel'
import useNodeMetadata from '../models/useNodeMetadata'
import useOrganizations from '../models/useOrganizations'
import useUsage, { periodLabel, totalTraffic } from '../models/useUsage'
import { formatLocation } from '../utilities/service'
import useSetPlaces from '../models/useSetPlaces'
import { placeName } from '../utilities/setPlaces'
import NodeSetPlace from './NodeSetPlace'
import { formatNodeAddress } from '../utilities/general'
import ChannelSelect from './ChannelSelect'
import DeleteConfirm from './DeleteConfirm'
import BackListItem from './BackListItem'
import Typography from '@mui/material/Typography'
import MuiLink from '@mui/material/Link'
import Timestamp from './Timestamp'
import IconNode from './IconNode'
import NodeData from './NodeData'
import TrafficMatrix from './TrafficMatrix'
import TrafficAllowance from './TrafficAllowance'
import NodeServices from './NodeServices'
import NodeLLMLinks from './NodeLLMLinks'
import NodeGrant from './NodeGrant'
import NodeAccess from './NodeAccess'
import StorageSection from './StorageSection'
import IconCopyButton from './IconCopyButton'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import useTerminal from '../models/useTerminal'
import NodeComputeSection from './NodeComputeSection'
import NodeTransfer from './NodeTransfer'
import { nodeHistoryHref } from '../utilities/auditLog'
import useOutsideNodes from '../hooks/useOutsideNodes'
import useServices from '../models/useServices'
import { nodePagePath } from '../utilities/nodeGroups'
import { nodeNameIn } from '../utilities/nodeNetworks'
import NodeNetworks from './NodeNetworks'
import useCloseWhenMissing, { HOME } from '../hooks/useCloseWhenMissing'
import useNetwork from '../models/useNetwork'
import usePhoneNode, { PHONE_REMOVE_UNAVAILABLE, usePhoneNodeId } from '../models/usePhoneNode'

const Node = () => {
  const { nodeId } = useParams<{ nodeId: string }>()
  const { nodes, networks, sessions } = useMergedData()
  // ⚠ A node of the active org in another org's network, or in no network (app#388), is not in
  // `useMergedData`: its network is not loaded. Its page reads it from `useOutsideNodes`, and its
  // services from the store, which holds them for every loaded node.
  const outside = useOutsideNodes().find(o => o.node.id === nodeId)
  const services = useServices(state => state.services)
  const { delete: deleteNode, setChannel, rename: renameNode } = useNode()
  const { tokens } = useToken()
  const { getByNodeId } = useNodeMetadata()
  const { channels } = useChannel()
  const openSession = useTerminal(state => state.openSession)
  const phoneId = usePhoneNodeId()
  const removePhone = usePhoneNode(s => s.remove)
  const loadNodeTraffic = useUsage(state => state.loadNodeTraffic)
  // The org-wide allowance axes, for the standing line under the matrix (app#86). The node view
  // never loaded them before — only /usage and the network view did — so a node page could show
  // bytes with nothing to measure them against. The store dedupes and keeps a 15 s freshness
  // window, so opening node after node does not re-run the month-scan RPCs.
  const loadUsage = useUsage(state => state.get)
  const traffic = useUsage(state => (nodeId ? state.nodeTraffic[nodeId] : undefined))
  const period = useUsage(state => state.period)
  const activeOrgId = useOrganizations(s => s.activeOrgId)
  const node = nodes.find(node => node.id === nodeId) ?? outside?.node
  // ⚠ A node that is not a node of the active org closes this page (app#473): after a reload on
  // the page of a node of the org the reader left, the pane was empty.
  // ⚠ It waits for the NETWORK list too: a node in a network is drawn from both lists, and on a
  // reload the nodes can arrive first. Without the wait the page asked about a node that was
  // about to draw.
  const nodesOrgId = useNode(state => state.loadedOrgId)
  const networksOrgId = useNetwork(state => state.loadedOrgId)
  const networksLoaded = !!activeOrgId && networksOrgId === activeOrgId
  useCloseWhenMissing({
    id: nodeId,
    found: !!node,
    loadedOrgId: networksLoaded ? nodesOrgId : undefined,
    orgOf: useNode(state => state.orgOf),
    to: HOME,
  })
  // ⚠⚠ THE ROLE IN THE NODE'S TENANT, `node.ownerOrgId`, NOT IN THE ACTIVE ORG (G-1, app#385,
  // docs/PERMISSIONS.md § 2). A node in a network of the active org can belong to another org, and
  // the router asks that org for every control on this page. `null` when the row carries no tenant:
  // no role, so the controls are hidden rather than shown on a guess.
  const { can, controls } = useOrgRole(node ? (node.ownerOrgId ?? null) : undefined)
  // The History link opens the ACTIVE org's audit log, so it keeps the active org's gate.
  const { can: canInActiveOrg } = useOrgRole()
  const compute = useComputeNodes().get(nodeId ?? '')
  const canNodeStorage = can('node.storage')
  // The refusal of the last channel change or removal, as one sentence (app#373).
  const [channelError, setChannelError] = useState<string | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)
  // The audit log is Owner/Admin (`org_audit_list` needs `members.manage`), so the History link
  // uses the gate `Audit.tsx` uses. For a Member it could only open a page that says no.
  const canViewHistory = canInActiveOrg('members.manage')
  // Depend on activeOrgId: on a hard refresh the node route mounts before the org store hydrates,
  // so loadNodeTraffic early-returns on a null org and never retries. Re-run once the org lands.
  useEffect(() => {
    if (!activeOrgId) return
    if (nodeId) void loadNodeTraffic(nodeId)
    void loadUsage()
  }, [nodeId, activeOrgId, loadNodeTraffic, loadUsage])
  const nodeMetadata = node ? getByNodeId(node.id) : undefined
  // A place a person set wins over the place of the address (nobgp-router#1976).
  const setPlace = useSetPlaces(s => (node ? s.places.find(place => place.nodeId === node.id) : undefined))
  // ⚠ The first loaded network the node joined: what this page needs ONE network for (the key, the
  // channel a node follows, the version floor, the Restore form). The Networks section shows them all.
  const nodeNetworks = node?.networkIds.flatMap(id => networks.filter(network => network.id === id)) ?? []
  const network = nodeNetworks[0]
  const token = tokens.find(token => token.networkId === network?.id)
  const connectedNodeIds = new Set(
    sessions.filter(s => s.proxyId === nodeId || s.gatewayId === nodeId).flatMap(s => [s.proxyId, s.gatewayId])
  )

  if (!node) return null

  const offline = isNodeOffline(node)
  // ⚠⚠ CONTROL OF THE NODE, NOT A ROLE (app#373, docs/PERMISSIONS.md § 4 and § 5 Node): the node's
  // OWNER or an Owner/Admin of the NODE'S org renames it, sets its channel, removes it, changes its
  // services and operates its compute. The org is the node's tenant, `node.ownerOrgId` (G-1,
  // app#385; Benoit, 2026-09-26), which is why `controls` comes from `useOrgRole(node.ownerOrgId)`
  // above. The rename was `node.rename` (Admin only), so a Member could not rename the node they
  // own; the channel and the services were any Member. The router asks the same rule at each call.
  const canManage = controls(node.ownerUserId)
  // What Remove Node does depends on the node (router `node_delete`): a provisioned node's compute
  // stops first; an ONLINE node registered by hand needs `force`, which also closes its connection.
  // The confirmation says which, and `force` is sent only for the case it names.
  const removeForce = !compute && !offline
  const removeMessage = node.id === phoneId
    ? 'This phone leaves noBGP: its node is deleted and its VPN is removed. Add this phone makes a new node.'
    : compute
    ? 'The machine that runs this node stops, and the node disappears from your list. If you provision a machine under the same name, it keeps its labels, grants and node storage.'
    : removeForce
      ? 'This node is online. The router closes its connection now, and refuses the node when it connects again. The agent on the machine keeps running. If you install the machine again with a key, it joins as a NEW node, without its labels, grants or node storage.'
      : 'The node disappears from your list, and the router refuses it when it connects again. If you install the machine again with a key, it joins as a NEW node, without its labels, grants or node storage. If you provision a machine under the same name, it keeps all three.'
  const onRemove = async () => {
    setRemoveError(null)
    // This phone, inside the noBGP app: its VPN goes and its identity is forgotten too, or the
    // agent would keep the deleted node's place (usePhoneNode.remove).
    try {
      if (node.id === phoneId) await removePhone()
      else await deleteNode(node.id, removeForce)
    } catch (e) {
      console.error('node_delete failed:', e)
      setRemoveError(e instanceof Error && e.message === PHONE_REMOVE_UNAVAILABLE ? e.message : nodeRemoveRefusal(e))
    }
  }
  const onChannel = async (channelId: string | null) => {
    setChannelError(null)
    try {
      await setChannel(node.id, channelId)
    } catch (e) {
      console.error('node_channel_set failed:', e)
      setChannelError(toolRefusalMessage(e, NODE_FORBIDDEN, 'Could not change the release channel.'))
    }
  }
  // ⚠⚠ ONE COLOUR, TWO SPELLINGS, AND NEITHER READER ACCEPTS THE OTHER'S. Both are
  // needed because the offline title is rendered two ways below.
  // `color` is the Typography PROP: it matches a variant built from the
  //   theme.palette.text KEYS, so it wants `textSecondary` and does nothing at all
  //   for `text.secondary` — measured, that spelling emits the same class as no
  //   color prop at all.
  // `sxColor` goes through `sx`, which resolves a palette PATH, so it wants
  //   `text.secondary` and leaves `textSecondary` as the literal declaration
  //   `color: textSecondary`, which the browser drops.
  // ⚠ The sx half was inert until now: TextFieldForm dropped the caller's
  // slotProps whole. With the merge in place, one spelling for both would have
  // dimmed an offline node's title for the user who cannot rename it and not for
  // the user who can — the same two-users-two-renderings defect this PR removes.
  const color = offline ? 'textSecondary' : 'inherit'
  const sxColor = offline ? 'text.secondary' : 'inherit'
  const connected = nodes.filter(n => connectedNodeIds.has(n.id) && n.id !== nodeId)
  const nodeServices = services.filter(s => s.nodeId === node.id)
  const nodeAddress = formatNodeAddress({ name: nodeNameIn(node, network?.id) }, network)
  // Operator metadata, minus the reserved role.* namespace — those carry the
  // local-MCP grant and surface in the Grants section below instead.
  const userLabels = Object.entries(node.labels ?? {}).filter(([key]) => !key.startsWith('role.'))

  return (
    <Stack sx={{
      gap: 3
    }}>
      <BackListItem to="/nodes">Nodes</BackListItem>
      <Box>
        {offline && <Chip label="Offline" size="small" sx={{ marginBottom: 0.5 }} />}
        {compute && <ComputeBadge compute={compute} sx={{ marginBottom: 0.5, marginLeft: offline ? 1 : 0 }} />}
        <RenameTitle
          value={node.name}
          subject="node"
          canRename={canManage}
          onRename={name => renameNode(node.id, name)}
          dnsLabel
          resetKey={nodeId}
          slotProps={{ input: { sx: { typography: 'h1', color: sxColor } } }}
          sx={{ maxWidth: 420 }}
          readOnly={
            <Typography variant="h1" color={color}>
              {node.name}
            </Typography>
          }
        />
        {/* ⚠ The address is `<node>.<network>`. With no loaded network (another org's network, or
            no network) the app cannot know it, and `<node>.` is not an address. A node in several
            networks has one address in each: the Networks section shows them. */}
        {network && node.networkIds.length === 1 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              color: 'text.secondary'
            }}>
            <LuLink2 size={16} />
            <Typography variant="code">{nodeAddress}</Typography>
            <IconCopyButton value={nodeAddress} />
          </Stack>
        )}
      </Box>
      <NodeVersionBanner node={node} network={network} />
      <NodeServices
        services={nodeServices}
        nodeId={nodeId!}
        canChange={canManage}
        onOpenTerminal={name => openSession({ serviceName: name, mode: 'modal' })}
      />
      {/* A compute node's machine, its controls and its task list (app#330). Renders nothing
          for a node that never ran a provisioned task. */}
      <NodeComputeSection nodeId={node.id} nodeName={node.name} networkId={network?.id} canOperate={canManage} />
      {/*
        The node's OWN storage, and only that. The network share is reached from
        the network pane — showing it here too was duplication, and the node page
        is the one place the node's own area has no other home.

        That area follows the MACHINE: it belongs to no network, survives the
        node moving between networks, and is retained if the node is deleted. The
        row used to say so ("This node's storage"), which made this section read
        differently from the identical one on the network page, for a fact nobody
        was reading off a list item. Recorded here instead.
      */}
      {/*
        ⚠ ROLE-GATED, and the whole section rather than the link. A node's own
        area needs Owner/Admin in the owning org — see `node.storage` in
        `utilities/orgRole.ts` — so for a Member this is a row that can only
        ever 403.
        Hiding the section and not just the link matters because
        `StorageSection` walks the tree for its size on mount: left rendered, a
        Member's every node page would fire a guaranteed-forbidden request and
        show a heading with no number under it.
        ⚠ The network share on `Network.tsx` is NOT gated this way and must not
        acquire this check — it is a different tree with a different rule.
      */}
      {canNodeStorage && (
        <StorageSection
          tree="nodes"
          id={node.id}
          linkState={{ backTo: nodePagePath(node.id), backLabel: node.name }}
        />
      )}
      {network && <NodeLLMLinks nodeAddress={nodeAddress} />}
      <NodeNetworks node={node} networks={networks} group={outside?.group} canManage={canManage} />
      <Box>
        <Typography variant="h6">Details</Typography>
        <NodeData
          icon={<LuMapPin />}
          data={[
            [setPlace ? placeName(setPlace) || `${setPlace.latitude.toFixed(2)}, ${setPlace.longitude.toFixed(2)}` : formatLocation(nodeMetadata)],
            ['Public IP:', nodeMetadata?.remoteAddr?.toString() || ''],
            ['Hostname:', nodeMetadata?.hostname || ''],
            // The credit that MaxMind's GeoLite license requires (nobgp/nobgp-router#1934): the place
            // on the first line comes from GeoLite data. No place, no credit.
            setPlace && node
              ? [
                  <NodeSetPlace
                    node={node}
                    addressPlace={nodeMetadata?.city || nodeMetadata?.state || nodeMetadata?.country ? formatLocation(nodeMetadata) : null}
                  />,
                ]
              : nodeMetadata?.city || nodeMetadata?.state || nodeMetadata?.country
              ? [
                  <Typography variant="caption" component="span" sx={{ color: 'text.secondary' }}>
                    Location data by{' '}
                    <MuiLink href="https://www.maxmind.com" target="_blank" rel="noreferrer" color="inherit">
                      MaxMind
                    </MuiLink>
                  </Typography>,
                ]
              : undefined,
          ]}
        />
        <NodeData
          icon={<LuCalendar />}
          data={[
            ['Created:', <Timestamp date={new Date(node.createdAt)} />],
            offline
              ? ['Offline:', node.offlineAt ? <Timestamp date={new Date(node.offlineAt)} /> : 'Never connected']
              : undefined,
          ]}
        />
        <NodeData icon={<LuKeySquare />} data={[[token?.name]]} />
        {!!userLabels.length && (
          <NodeData
            icon={<LuTags />}
            justifyIcon="center"
            data={[
              [
                <Stack
                  key="labels"
                  direction="row"
                  component="span"
                  sx={{ gap: 0.5, flexWrap: 'wrap' }}
                >
                  {userLabels.map(([key, value]) => (
                    <Chip
                      key={key}
                      size="small"
                      variant="outlined"
                      label={value === true ? key : `${key}: ${String(value)}`}
                    />
                  ))}
                </Stack>,
              ],
            ]}
          />
        )}
      </Box>
      {traffic !== undefined && (
        <Box>
          <Typography variant="h6">Traffic</Typography>
          {/* `undefined` is NOT fetched (or a transient RPC failure) and hides the whole section;
              `null` is fetched and genuinely empty. They are different facts and only the second
              one is safe to draw, which is why the gate is `!== undefined` and not truthiness — a
              node that moved nothing still gets the allowance line, because where the ORG stands
              is the part a quiet node's owner cannot work out from this page. Same split the
              network view already makes. */}
          {traffic ? (
            <TrafficMatrix traffic={traffic} caption={periodLabel(period)} />
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {periodLabel(period)} — no traffic recorded.
            </Typography>
          )}
          <TrafficAllowance scope="node" scopeBytes={traffic ? totalTraffic(traffic) : 0} />
        </Box>
      )}
      {!!connected.length && (
        <Box>
          <Typography variant="h6" sx={{
            marginBottom: 1
          }}>
            Connected
          </Typography>
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'flex-start'
            }}>
            {connected.map(n => (
              <Button to={nodePagePath(n.id)} component={Link} key={n.id}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                  <IconNode type="node" offline={isNodeOffline(n)} size={22} style={{ marginBottom: 8 }} />
                  {n.name}
                </Typography>
              </Button>
            ))}
          </Stack>
        </Box>
      )}
      <ChannelSelect
        value={node.channelId ?? null}
        channels={channels}
        onChange={channelId => void onChannel(channelId)}
        networkChannelId={network?.channelId ?? null}
        disabled={!canManage}
        error={channelError}
      />
      {/* The node's ONE access panel (router PERMISSIONS.md § 4.1, D-26, app#375): the shared
          setting, the access list and every service's visitors. Keyed on the node, so the view a
          refused read chose does not carry over to the next node. */}
      <Box>
        <Typography variant="h6" sx={{ marginBottom: 1 }}>
          Access
        </Typography>
        <NodeAccess key={`${node.id}:${node.networkIds.join()}`} node={node} />
      </Box>
      <Box>
        <Typography variant="h6" sx={{
          marginBottom: 1
        }}>
          Grants
        </Typography>
        <NodeGrant node={node} networkNames={nodeNetworks.flatMap(network => (network.name ? [network.name] : []))} />
      </Box>
      <Box>
        <Typography variant="h6" sx={{
          marginBottom: 2
        }}>
          Manage
        </Typography>
        {/* The audit log, searched for this node's id (app#348). */}
        {canViewHistory && (
          <Button
            component={Link}
            to={nodeHistoryHref(node.id)}
            variant="outlined"
            startIcon={<LuHistory />}
            size="small"
            sx={{ marginRight: 1 }}
          >
            History
          </Button>
        )}
        {/* The restore form adds the node to a network of the ACTIVE org, so it needs one. */}
        {offline && network && (
          <Button
            component={Link}
            to={`/node/${network.id}?name=${encodeURIComponent(nodeNameIn(node, network.id))}`}
            variant="outlined"
            startIcon={<LuUndo2 />}
            size="small"
            sx={{ marginRight: 1 }}
          >
            Restore Node
          </Button>
        )}
        {/* ⚠⚠ REMOVE IS NOT REVERSIBLE FROM HERE, and this said the opposite until
            app#258 ("if it comes back online it will be restored"). The remove is a soft
            delete, and `RestoreNode` — the one call that clears `deleted_at` — is reached
            only by `provision_node` (router `src/tools/provisioning.go`). Every
            registration lookup is soft-delete scoped: `GetNodeByNetworkIdAndKID`,
            `GetNodeByNetworkIdAndName` and `Node.match` all miss the removed row, so a
            machine that installs again with a KEY mints a NEW id and loses the labels,
            grants and `nodes/<node_id>/` storage that hung off the old one.
            ⚠⚠ PROVISIONING THE SAME NAME IS THE EXCEPTION, AND THE COPY MUST CARRY IT.
            `classifyProvisionName` finds the soft-deleted row and `provisioning.go` calls
            `RestoreNode` BEFORE the provider call, so a re-provision comes back on the SAME
            node id with all three intact — which is what `ProvisionedTasks.tsx` promises and
            what the Restore Node button above links to. Saying the loss is unconditional
            makes these two surfaces contradict each other.
            ⚠ It also cannot come back by itself. `VerifyAgentJWT` resolves the node
            through the scoped `GetNodeById`, so the next reconnect is refused, and the agent
            deleted its key from config at enrolment (`RemoveConfigKeys("network-key", ...)`,
            agent `src/command.go`) — it has nothing to re-enrol with and says so.
            ⚠ The live socket is torn down only for an ONLINE node registered by hand, where
            `node_delete` needs `force` and closes it (app#373). */}
        {/* Ownership transfer (app#376, router `node_transfer`): the node's controllers only.
            ⚠ The suggestions come from the node's tenant, `nodes.owner_org_id` (app#385), as in
            NodeAccess; the active org only when the tenant is not known. The router checks the new
            owner against the node's own org. */}
        {canManage && (
          <NodeTransfer nodeId={node.id} nodeName={node.name} ownerUserId={node.ownerUserId} orgId={node.ownerOrgId ?? activeOrgId} />
        )}
        {/* ⚠ The node's OWNER or an org Owner/Admin (app#371). The router's `node_delete` refuses
            everyone else (app#373); this only hides the control. Same shape as Delete Network in
            Network.tsx. */}
        {canManage ? (
          <DeleteConfirm onConfirm={() => void onRemove()} message={removeMessage}>
            <Button variant="outlined" color="error" startIcon={<LuX />} size="small">
              Remove Node
            </Button>
          </DeleteConfirm>
        ) : (
          <Tooltip title="Only the owner of this node, or an organization owner or admin, can remove it">
            <span>
              <Button variant="outlined" color="error" startIcon={<LuX />} size="small" disabled>
                Remove Node
              </Button>
            </span>
          </Tooltip>
        )}
        {removeError && (
          <Alert severity="error" sx={{ marginTop: 2 }}>
            {removeError}
          </Alert>
        )}
      </Box>
    </Stack>
  )
}

export default Node
